import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingMoreTechniciansIndicator } from './LoadingMoreTechniciansIndicator'
import { MonthDayJobsPopover } from './MonthDayJobsPopover'
import { TechnicianQuickActions } from './TechnicianQuickActions'
import {
  clearAgendaDragPayload,
  getAgendaDragPayload,
  updateAgendaDragPreview,
  type AgendaDragPayload,
} from './agenda-dnd'
import { agendaDropTargetClass } from './agenda-drop-feedback'
import {
  sameDay,
  toISODate,
  uniqueJobsWithLane,
  type JobWithLane,
  type TechnicianLane,
} from './grid-helpers'
import {
  PRELOAD_DATE_ATTR,
  PRELOAD_IDS_ATTR,
  PRELOAD_ROOT_PROPS,
} from './job-detail-preload'
import {
  DEFAULT_DAY_START_HOUR,
  computeDropTimeRange,
  minutesToTimeString,
} from './time-geometry'
import type { Job } from '@/types'
import type { AgendaMutationHandlers } from '@/components/jobs/AgendaGrid'

interface MonthViewProps extends Partial<AgendaMutationHandlers> {
  days: Date[]
  lanes: TechnicianLane[]
  jobsByTech: Map<string, Job[]>
  onEntryClick: (job: Job) => void
  onDayClick?: (day: Date) => void
  onLoadMoreTechnicians?: () => void
  hasMoreTechnicians?: boolean
  isLoadingMoreTechnicians?: boolean
  enableTechnicianColumnReorder?: boolean
  enableDragDropAssignment?: boolean
  enableResizeDuration?: boolean
}

const MONTH_POPOVER_CLOSE_DELAY_MS = 160
const LOAD_MORE_TECHNICIANS_THRESHOLD_PX = 720
type DroppableAgendaDragPayload = Exclude<
  AgendaDragPayload,
  { type: 'technician-column' }
>

function laneJobsForDay(
  jobsWithLane: JobWithLane[],
  lane: TechnicianLane,
  day: Date
) {
  return jobsWithLane
    .filter(
      ({ job, lane: jobLane }) =>
        jobLane.technicianId === lane.technicianId &&
        Boolean(job.scheduled_date && sameDay(job.scheduled_date, day))
    )
    .sort((a, b) =>
      (a.job.start_time ?? '').localeCompare(b.job.start_time ?? '')
    )
}

function nextTechnicianIdsForDrop(
  payload: Extract<AgendaDragPayload, { type: 'scheduled-job' }>,
  targetTechnicianId: string
) {
  const currentIds =
    payload.technicianIds.length > 0
      ? payload.technicianIds
      : [payload.fromTechnicianId]

  if (payload.fromTechnicianId === targetTechnicianId) {
    return Array.from(new Set(currentIds))
  }

  const nextIds = currentIds.map(id =>
    id === payload.fromTechnicianId ? targetTechnicianId : id
  )
  if (!nextIds.includes(targetTechnicianId)) nextIds.push(targetTechnicianId)

  return Array.from(new Set(nextIds))
}

function timeRangeForDropPayload(payload: DroppableAgendaDragPayload) {
  if (payload.startTime && payload.endTime) {
    return { start_time: payload.startTime, end_time: payload.endTime }
  }

  const { start, end } = computeDropTimeRange({
    pointerMinutes: DEFAULT_DAY_START_HOUR * 60,
    dayStartHour: DEFAULT_DAY_START_HOUR,
    durationMinutes: payload.durationMinutes,
  })

  return {
    start_time: minutesToTimeString(start),
    end_time: minutesToTimeString(end),
  }
}

export function MonthView({
  days,
  lanes,
  jobsByTech,
  onEntryClick,
  onDayClick,
  onScheduleJob,
  isMutatingJobId,
  onLoadMoreTechnicians,
  hasMoreTechnicians = false,
  isLoadingMoreTechnicians = false,
  enableDragDropAssignment = true,
}: MonthViewProps) {
  const jobsWithLane = uniqueJobsWithLane(lanes, jobsByTech)
  const todayIso = toISODate(new Date())
  const [activeCell, setActiveCell] = useState<string | null>(null)
  const [activeCellElement, setActiveCellElement] =
    useState<HTMLElement | null>(null)
  const [dropCellId, setDropCellId] = useState<string | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const lastScrollTopRef = useRef(0)
  const canDragDropAssignments =
    enableDragDropAssignment && Boolean(onScheduleJob)

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const openCell = useCallback(
    (cellId: string, cellElement?: HTMLElement) => {
      clearCloseTimer()
      if (cellElement) setActiveCellElement(cellElement)
      setActiveCell(cellId)
    },
    [clearCloseTimer]
  )

  const scheduleCloseCell = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setActiveCellElement(null)
      setActiveCell(null)
    }, MONTH_POPOVER_CLOSE_DELAY_MS)
  }, [clearCloseTimer])

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (
        !hasMoreTechnicians ||
        isLoadingMoreTechnicians ||
        !onLoadMoreTechnicians
      ) {
        return
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
      const lastScrollTop = lastScrollTopRef.current
      lastScrollTopRef.current = scrollTop

      if (scrollHeight <= clientHeight || scrollTop <= lastScrollTop) return

      if (
        scrollHeight - scrollTop - clientHeight <=
        LOAD_MORE_TECHNICIANS_THRESHOLD_PX
      ) {
        onLoadMoreTechnicians()
      }
    },
    [hasMoreTechnicians, isLoadingMoreTechnicians, onLoadMoreTechnicians]
  )

  const getSchedulePayload = (dataTransfer: DataTransfer) => {
    if (!canDragDropAssignments) return null
    const payload = getAgendaDragPayload(dataTransfer)
    return payload && payload.type !== 'technician-column' ? payload : null
  }

  const daysInMonth = useMemo(() => {
    const anchor = days[0] ?? new Date()
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
    return Array.from({ length: end.getDate() }, (_, index) => {
      const day = new Date(start)
      day.setDate(index + 1)
      return day
    })
  }, [days])

  return (
    <div
      className='h-[calc(100vh-250px)] min-h-[560px] overflow-auto bg-white xl:h-auto xl:min-h-0 xl:flex-1'
      {...PRELOAD_ROOT_PROPS}
      onScroll={handleScroll}
    >
      <div
        className='flex w-full flex-col xl:h-full'
        style={{ minWidth: 200 + daysInMonth.length * 48 }}
      >
        <div className='sticky top-0 z-30 flex shrink-0 bg-white'>
          <div className='sticky left-0 z-40 h-16 w-[200px] shrink-0 border-b border-r border-[#e7e9ef] bg-white shadow-[1px_0_4px_rgba(26,27,30,0.08)]' />
          {daysInMonth.map(day => {
            const iso = toISODate(day)
            return (
              <button
                type='button'
                key={iso}
                onClick={() => onDayClick?.(new Date(day))}
                className={`flex h-16 min-w-12 flex-1 items-center justify-center border-b border-r border-[#e7e9ef] bg-white font-sans text-base font-medium leading-none tabular-nums outline-none transition-colors hover:bg-[#fbfbfc] focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-[-2px] ${
                  iso === todayIso ? 'text-[#ff5a1f]' : 'text-[#171a20]'
                }`}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>

        {lanes.map(lane => {
          const laneTotal = jobsWithLane.filter(
            entry => entry.lane.technicianId === lane.technicianId
          ).length

          return (
            <div key={lane.technicianId} className='flex min-h-16'>
              <TechnicianRowHeader lane={lane} total={laneTotal} />
              {daysInMonth.map(day => {
                const iso = toISODate(day)
                const cellId = `${lane.technicianId}-${iso}`
                const entries = laneJobsForDay(jobsWithLane, lane, day)
                const isActive = activeCell === cellId
                const isDropActive = dropCellId === cellId
                const isDropBlocked = isDropActive && Boolean(isMutatingJobId)
                const popoverSide = day.getDate() > 18 ? 'right' : 'left'
                // The month grid has no job cards; today's cells stand in as
                // the background preload targets (jobs most likely opened).
                const preloadTargetProps =
                  iso === todayIso && entries.length > 0
                    ? {
                        [PRELOAD_IDS_ATTR]: entries
                          .map(entry => entry.job.id)
                          .join(','),
                        [PRELOAD_DATE_ATTR]: iso,
                      }
                    : null

                return (
                  <div
                    key={cellId}
                    role='button'
                    tabIndex={0}
                    {...preloadTargetProps}
                    onMouseEnter={event =>
                      entries.length > 0 &&
                      openCell(cellId, event.currentTarget)
                    }
                    onMouseLeave={() =>
                      entries.length > 0 && scheduleCloseCell()
                    }
                    onDragEnter={
                      canDragDropAssignments
                        ? event => {
                            if (getSchedulePayload(event.dataTransfer)) {
                              clearCloseTimer()
                              setDropCellId(cellId)
                            }
                          }
                        : undefined
                    }
                    onDragOver={
                      canDragDropAssignments
                        ? event => {
                            const payload = getSchedulePayload(
                              event.dataTransfer
                            )
                            if (!payload) return
                            event.preventDefault()
                            event.dataTransfer.dropEffect = isDropBlocked
                              ? 'none'
                              : 'move'
                            updateAgendaDragPreview(
                              event.clientX,
                              event.clientY,
                              isDropBlocked ? null : lane.name
                            )
                            setDropCellId(cellId)
                          }
                        : undefined
                    }
                    onDragLeave={
                      canDragDropAssignments
                        ? event => {
                            if (
                              !event.currentTarget.contains(
                                event.relatedTarget as Node
                              )
                            ) {
                              setDropCellId(current =>
                                current === cellId ? null : current
                              )
                            }
                          }
                        : undefined
                    }
                    onDrop={
                      canDragDropAssignments
                        ? event => {
                            const payload = getSchedulePayload(
                              event.dataTransfer
                            )
                            if (!payload) return
                            event.preventDefault()
                            setDropCellId(null)
                            if (isDropBlocked || !onScheduleJob) {
                              clearAgendaDragPayload()
                              return
                            }

                            const timeRange = timeRangeForDropPayload(payload)
                            onScheduleJob(payload.jobId, {
                              scheduled_date: iso,
                              ...timeRange,
                              technician_ids:
                                payload.type === 'scheduled-job'
                                  ? nextTechnicianIdsForDrop(
                                      payload,
                                      lane.technicianId
                                    )
                                  : [lane.technicianId],
                            })
                            clearAgendaDragPayload()
                          }
                        : undefined
                    }
                    onFocus={event =>
                      entries.length > 0 &&
                      openCell(cellId, event.currentTarget)
                    }
                    onBlur={event => {
                      const next = event.relatedTarget as Node | null
                      if (next && event.currentTarget.contains(next)) return
                      scheduleCloseCell()
                    }}
                    onClick={() => onDayClick?.(new Date(day))}
                    onKeyDown={event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onDayClick?.(new Date(day))
                    }}
                    className={`relative flex min-h-16 min-w-12 flex-1 cursor-pointer items-center justify-center border-b border-r border-[#e7e9ef] font-sans text-base font-medium leading-none tabular-nums text-[#171a20] outline-none ${agendaDropTargetClass(isDropActive, isDropBlocked)} ${
                      entries.length > 0 ? '' : 'text-[#69758c]'
                    } ${
                      isActive
                        ? 'z-[80] bg-white'
                        : entries.length > 0
                          ? ''
                          : 'bg-white'
                    }`}
                    style={{
                      backgroundColor:
                        entries.length > 0 && !isActive ? lane.tint : undefined,
                    }}
                  >
                    <span className='relative z-10'>
                      {entries.length > 0 ? entries.length : '-'}
                    </span>
                    {isActive && (
                      <span
                        aria-hidden='true'
                        className='pointer-events-none absolute inset-0 z-20 border border-[#ff5a1f]'
                      />
                    )}
                    {entries.length > 0 && (
                      <MonthDayJobsPopover
                        jobs={entries.map(entry => entry.job)}
                        onViewJob={onEntryClick}
                        open={isActive}
                        anchorElement={isActive ? activeCellElement : null}
                        pointerSide={popoverSide}
                        onMouseEnter={() => openCell(cellId)}
                        onMouseLeave={scheduleCloseCell}
                        onClick={event => event.stopPropagation()}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}

        {isLoadingMoreTechnicians && (
          <LoadingMoreTechniciansIndicator
            loading={isLoadingMoreTechnicians}
            className='sticky left-0 z-20 h-14 w-full border-t border-[#e7e9ef] bg-white/95'
          />
        )}
      </div>
    </div>
  )
}

function TechnicianRowHeader({
  lane,
  total,
}: {
  lane: TechnicianLane
  total: number
}) {
  const { t } = useTranslation('jobs')

  return (
    <div
      className='sticky left-0 z-20 flex min-h-16 w-[200px] shrink-0 flex-col justify-center gap-2 border-b border-r border-[#e7e9ef] bg-white px-3 shadow-[1px_0_4px_rgba(26,27,30,0.08)]'
      style={{ boxShadow: `inset -2px 0 0 ${lane.color}` }}
    >
      <div className='flex min-w-0 items-center gap-2'>
        <span
          className='h-3 w-3 shrink-0 rounded-full'
          style={{ backgroundColor: lane.color }}
        />
        <span className='min-w-0 flex-1 truncate font-sans text-base font-medium text-[#171a20]'>
          {lane.name}
        </span>
        <TechnicianQuickActions lane={lane} />
      </div>
      <span className='text-left font-sans text-xs font-normal leading-4 text-[#69758c]'>
        {t('agenda.totalShort', { count: total })}
      </span>
    </div>
  )
}
